import { useEffect, useState, useCallback } from 'react';
import { Camera, Bell, Wifi, Tv, Phone, MapPin, Clock, ChevronRight, Menu, X, Star, Shield, Zap, Calendar, Mail, Facebook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getServices, getGallery, getSettings, createQuote, seedData, getRecentWorks } from '@/lib/api';

const iconMap = {
  Camera: Camera,
  Bell: Bell,
  Wifi: Wifi,
  Tv: Tv,
};

// Hook for scroll reveal animations - triggers every time element enters viewport
const useScrollReveal = (dependencies = []) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('revealed');
            } else {
              // Remove class when out of view so it can animate again
              entry.target.classList.remove('revealed');
            }
          });
        },
        {
          threshold: 0.15,
          rootMargin: '0px 0px -50px 0px'
        }
      );

      const elements = document.querySelectorAll('.scroll-reveal');
      elements.forEach((el) => observer.observe(el));

      return () => {
        elements.forEach((el) => observer.unobserve(el));
      };
    }, 100);

    return () => clearTimeout(timer);
  }, dependencies);
};

// Helper do pobierania URL obrazu
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const Navbar = ({ settings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Start', href: '#hero' },
    { label: 'Usługi', href: '#services' },
    { label: 'Realizacje', href: '#gallery' },
    { label: 'Ostatnie prace', href: '#recent-works' },
    { label: 'O nas', href: '#about' },
    { label: 'Kontakt', href: '#contact' },
  ];

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0A0F1C]/95 backdrop-blur-md border-b border-cyan-500/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#hero" className="flex items-center gap-3" data-testid="logo-link">
            {settings.logo_url ? (
              <img 
                src={getImageUrl(settings.logo_url)} 
                alt={settings.name} 
                className="h-20 md:h-28 w-auto object-contain"
                style={{ 
                  filter: 'brightness(1.1) drop-shadow(0 0 12px rgba(0, 212, 255, 0.6))',
                  transform: 'scaleX(1.5)',
                  transformOrigin: 'left center',
                }}
              />
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-lg flex items-center justify-center shadow-lg shadow-cyan-500/30">
                  <Camera className="w-5 h-5 text-[#0A0F1C]" />
                </div>
                <span className="font-bold text-xl text-white tracking-tight">{settings.name}</span>
              </div>
            )}
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link text-slate-400 hover:text-cyan-400 font-medium transition-colors"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a href="#contact">
              <Button 
                className="h-11 px-6 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600 text-[#0A0F1C] hover:from-cyan-300 hover:to-cyan-500 font-semibold shadow-lg shadow-cyan-500/30"
                data-testid="nav-cta-button"
              >
                Bezpłatna wycena
              </Button>
            </a>
          </div>

          <button
            className="md:hidden p-2 text-white"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden bg-[#111827] border-t border-cyan-500/10 py-4" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 px-4 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/5 font-medium"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <div className="px-4 pt-4">
              <a href="#contact" onClick={() => setIsOpen(false)}>
                <Button className="w-full h-11 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600 text-[#0A0F1C] font-semibold">
                  Bezpłatna wycena
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const HeroSection = ({ settings }) => (
  <section 
    id="hero" 
    className="relative min-h-screen flex items-center pt-20 bg-[#0A0F1C] grid-pattern overflow-hidden"
    data-testid="hero-section"
  >
    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[120px]" />
    <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/15 rounded-full blur-[100px]" />
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 relative z-10">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="space-y-8 animate-slide-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full">
            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            <span className="text-sm font-medium text-cyan-400">Ocena 5.0 na Google</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight text-white leading-[1.1]">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-cyan-200">
              {settings.tagline?.split(' ')[0] || 'Profesjonalne'}
            </span>
            <br />
            <span className="text-3xl sm:text-4xl md:text-6xl">
              {settings.tagline?.split(' ').slice(1).join(' ') || 'Systemy Bezpieczeństwa'}
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-xl">
            {settings.description || 'Instalacje, wideofony, monitoring, sieci komputerowe, LAN, TV - doradztwo, projekt, montaż.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="#contact">
              <Button 
                className="h-14 px-8 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600 text-[#0A0F1C] hover:from-cyan-300 hover:to-cyan-500 text-lg font-semibold btn-primary shadow-lg shadow-cyan-500/30"
                data-testid="hero-cta-button"
              >
                Zapytaj o wycenę
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <a href="#services">
              <Button 
                variant="outline" 
                className="h-14 px-8 rounded-full border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/50 text-lg font-medium bg-transparent"
                data-testid="hero-services-button"
              >
                Zobacz usługi
              </Button>
            </a>
          </div>
          <div className="flex flex-wrap items-center gap-4 md:gap-6 pt-4">
            <div className="flex items-center gap-2 text-slate-400">
              <Phone className="w-5 h-5 text-cyan-400" />
              <span className="font-medium">{settings.phone}</span>
            </div>
            {settings.email && (
              <a href={`mailto:${settings.email}`} className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors">
                <Mail className="w-5 h-5 text-cyan-400" />
                <span className="font-medium">{settings.email}</span>
              </a>
            )}
            <div className="flex items-center gap-2 text-slate-400">
              <Clock className="w-5 h-5 text-cyan-400" />
              <span>{settings.hours}</span>
            </div>
          </div>
        </div>
        <div className="relative animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="relative rounded-2xl overflow-hidden border border-cyan-500/20 shadow-2xl shadow-cyan-500/10">
            <img
              src={settings.hero_image_url ? getImageUrl(settings.hero_image_url) : "https://images.unsplash.com/photo-1640826028382-e209fad88fa7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"}
              alt="System monitoringu"
              className="w-full h-auto object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/80 to-transparent" />
            {/* Badge Szybki montaż */}
            <div className="absolute top-3 right-3 bg-[#111827]/90 backdrop-blur-sm rounded-lg p-2 shadow-xl border border-cyan-500/30">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-cyan-400" />
                <span className="text-xs font-medium text-white">Szybki montaż</span>
              </div>
            </div>
            {/* Badge Podgląd 24/7 */}
            <div className="absolute bottom-3 left-3 bg-[#111827]/90 backdrop-blur-sm rounded-lg p-2 shadow-xl border border-cyan-500/30">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-cyan-400" />
                <div>
                  <p className="text-xs font-semibold text-white">Podgląd 24/7</p>
                  <p className="text-[10px] text-slate-400">Na smartfonie</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ServicesSection = ({ services }) => (
  <section id="services" className="py-24 md:py-32 bg-[#0D1221]" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-4">Nasze usługi</p>
        <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white">
          Kompleksowe rozwiązania
        </h2>
        <p className="mt-4 text-lg text-slate-400 max-w-2xl mx-auto">
          Od projektu do realizacji - zapewniamy pełne wsparcie na każdym etapie
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
        {services.map((service) => {
          const Icon = iconMap[service.icon] || Camera;
          return (
            <div
              key={service.id}
              className="service-card rounded-2xl card-hover scroll-reveal overflow-hidden group"
              data-testid={`service-card-${service.id}`}
            >
              {service.image_url ? (
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={getImageUrl(service.image_url)} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111827] via-[#111827]/50 to-transparent" />
                  <div className="absolute top-4 right-4 w-10 h-10 bg-[#0A0F1C]/80 backdrop-blur-sm border border-cyan-500/30 rounded-lg flex items-center justify-center group-hover:bg-cyan-500 group-hover:border-cyan-400 transition-all duration-300">
                    <Icon className="w-5 h-5 text-cyan-400 group-hover:text-[#0A0F1C] transition-colors" />
                  </div>
                </div>
              ) : (
                <div className="p-8 pb-0">
                  <div className="icon-container mb-6">
                    <Icon className="w-6 h-6 text-cyan-400 transition-colors" />
                  </div>
                </div>
              )}
              <div className={service.image_url ? "p-6" : "p-8 pt-0"}>
                <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                <p className="text-slate-400 leading-relaxed">{service.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const GallerySection = ({ gallery }) => {
  return (
    <section id="gallery" className="py-24 md:py-32 bg-[#0A0F1C]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-4">Portfolio</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white">
            Nasze realizacje
          </h2>
          <p className="mt-4 text-lg text-slate-400 max-w-2xl mx-auto">
            Zobacz przykłady naszych projektów dla klientów indywidualnych i firm
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gallery.map((item) => (
            <div
              key={item.id}
              className="group rounded-2xl overflow-hidden border border-cyan-500/10 card-hover scroll-reveal bg-[#111827]"
              data-testid={`gallery-item-${item.id}`}
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={getImageUrl(item.image_url)}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111827] via-transparent to-transparent" />
              </div>
              <div className="p-5">
                <span className="inline-block px-3 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-semibold rounded-full mb-3">
                  {item.category}
                </span>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const RecentWorksSection = ({ recentWorks }) => {
  if (!recentWorks || recentWorks.length === 0) {
    return null;
  }
  
  return (
    <section id="recent-works" className="py-24 md:py-32 bg-[#0D1221]" data-testid="recent-works-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-4">Ostatnie prace</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white">
            Nasze ostatnie realizacje
          </h2>
          <p className="mt-4 text-lg text-slate-400 max-w-2xl mx-auto">
            Zobacz najnowsze projekty zrealizowane dla naszych klientów
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {recentWorks.map((work) => (
            <div
              key={work.id}
              className="group rounded-2xl overflow-hidden card-hover scroll-reveal bg-[#111827] border border-cyan-500/10"
              data-testid={`recent-work-${work.id}`}
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={getImageUrl(work.image_url)}
                  alt={work.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-slate-400">
                    {new Date(work.created_at).toLocaleDateString('pl-PL', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{work.title}</h3>
                <p className="text-slate-400 leading-relaxed">{work.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutSection = ({ settings }) => (
  <section id="about" className="py-24 md:py-32 bg-[#0A0F1C]" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-4">O nas</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-6">
            Doświadczenie i profesjonalizm
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed mb-8">
            {settings.about_text || 'CAM VISION to firma z wieloletnim doświadczeniem w branży bezpieczeństwa i instalacji elektronicznych. Specjalizujemy się w kompleksowych rozwiązaniach dla domu i firmy.'}
          </p>
          <div className="grid grid-cols-2 gap-6">
            {[
              { value: '10+', label: 'Lat doświadczenia' },
              { value: '500+', label: 'Zrealizowanych projektów' },
              { value: '5.0', label: 'Ocena Google' },
              { value: '100%', label: 'Zadowolonych klientów' },
            ].map((stat) => (
              <div key={stat.label} className="p-6 bg-[#111827] rounded-xl border border-cyan-500/10">
                <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-cyan-200 mb-2">
                  {stat.value}
                </p>
                <p className="text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl blur-2xl" />
          <img
            src="https://images.unsplash.com/photo-1558002038-1055907df827?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
            alt="Nasza praca"
            className="relative rounded-2xl border border-cyan-500/20 shadow-xl"
          />
        </div>
      </div>
    </div>
  </section>
);

const ContactSection = ({ settings, services }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service_type: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.service_type) {
      toast.error('Wypełnij wszystkie wymagane pola');
      return;
    }
    setIsSubmitting(true);
    try {
      await createQuote(formData);
      toast.success('Dziękujemy! Skontaktujemy się wkrótce.');
      setFormData({ name: '', phone: '', service_type: '', message: '' });
    } catch (error) {
      toast.error('Wystąpił błąd. Spróbuj ponownie.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#0D1221]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-4">Kontakt</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-6">
              Zapytaj o wycenę
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed mb-8">
              Skontaktuj się z nami, aby omówić swój projekt. Bezpłatna wycena i doradztwo.
            </p>

            <div className="space-y-6">
              {[
                { icon: Phone, label: 'Telefon', value: settings.phone, href: `tel:${settings.phone}` },
                { icon: Mail, label: 'Email', value: settings.email, href: settings.email ? `mailto:${settings.email}` : null, show: !!settings.email },
                { icon: Facebook, label: 'Facebook', value: 'camvision.pl', href: 'https://www.facebook.com/camvision.pl', external: true },
                { icon: MapPin, label: 'Adres', value: settings.address },
                { icon: Clock, label: 'Godziny pracy', value: settings.hours },
              ].filter(item => item.show !== false).map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400/20 to-cyan-600/20 border border-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white mb-1">{item.label}</p>
                    {item.href ? (
                      <a 
                        href={item.href} 
                        className="text-slate-400 hover:text-cyan-400 transition-colors"
                        {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-slate-400">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#111827] rounded-2xl p-8 border border-cyan-500/10">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Imię i nazwisko *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 rounded-lg bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500 focus:border-cyan-400"
                  placeholder="Jan Kowalski"
                  data-testid="quote-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Numer telefonu *
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-12 rounded-lg bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500 focus:border-cyan-400"
                  placeholder="+48 123 456 789"
                  data-testid="quote-phone-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Rodzaj usługi *
                </label>
                <Select
                  value={formData.service_type}
                  onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                >
                  <SelectTrigger className="h-12 rounded-lg bg-[#0A0F1C] border-cyan-500/20 text-white" data-testid="quote-service-select">
                    <SelectValue placeholder="Wybierz usługę" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-cyan-500/20">
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.title} className="text-white hover:bg-cyan-500/10">
                        {service.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="Inne" className="text-white hover:bg-cyan-500/10">Inne</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Wiadomość
                </label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="rounded-lg bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500 focus:border-cyan-400 min-h-[120px]"
                  placeholder="Opisz swój projekt..."
                  data-testid="quote-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600 text-[#0A0F1C] hover:from-cyan-300 hover:to-cyan-500 text-lg font-semibold btn-primary shadow-lg shadow-cyan-500/30"
                data-testid="quote-submit-button"
              >
                {isSubmitting ? 'Wysyłanie...' : 'Wyślij zapytanie'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ settings }) => (
  <footer className="bg-[#070A12] text-white py-16 border-t border-cyan-500/10" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-3 gap-12">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-lg flex items-center justify-center">
              <Camera className="w-5 h-5 text-[#0A0F1C]" />
            </div>
            <span className="font-bold text-xl">{settings.name}</span>
          </div>
          <p className="text-slate-400 leading-relaxed">
            {settings.description}
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-lg mb-4 text-cyan-400">Usługi</h4>
          <ul className="space-y-3 text-slate-400">
            <li className="hover:text-cyan-400 transition-colors cursor-pointer">Monitoring CCTV</li>
            <li className="hover:text-cyan-400 transition-colors cursor-pointer">Wideofony</li>
            <li className="hover:text-cyan-400 transition-colors cursor-pointer">Sieci LAN & WiFi</li>
            <li className="hover:text-cyan-400 transition-colors cursor-pointer">Instalacje TV</li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-lg mb-4 text-cyan-400">Kontakt</h4>
          <ul className="space-y-3 text-slate-400">
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-cyan-400" />
              {settings.phone}
            </li>
            {settings.email && (
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-cyan-400" />
                <a href={`mailto:${settings.email}`} className="hover:text-cyan-400 transition-colors">
                  {settings.email}
                </a>
              </li>
            )}
            <li className="flex items-center gap-2">
              <Facebook className="w-4 h-4 text-cyan-400" />
              <a 
                href="https://www.facebook.com/camvision.pl" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-cyan-400 transition-colors"
              >
                camvision.pl
              </a>
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-cyan-400" />
              {settings.address}
            </li>
            <li className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-cyan-400" />
              {settings.hours}
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-cyan-500/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-slate-500 text-sm">
          © {new Date().getFullYear()} {settings.name}. Wszelkie prawa zastrzeżone.
        </p>
        <a 
          href="/admin" 
          className="text-slate-500 hover:text-cyan-400 text-sm transition-colors"
          data-testid="admin-link"
        >
          Panel administratora
        </a>
      </div>
    </div>
  </footer>
);

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [recentWorks, setRecentWorks] = useState([]);
  const [settings, setSettings] = useState({
    name: 'CAM VISION',
    tagline: 'Profesjonalne Systemy Bezpieczeństwa',
    description: 'Instalacje, wideofony, monitoring, sieci komputerowe, LAN, TV - doradztwo, projekt, montaż.',
    phone: '+48 530 500 047',
    address: 'Storczykowa, 05-500 Zgorzała, Poland',
    hours: 'Pon-Sob 8:00 - 17:00',
    logo_url: '',
    about_text: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await seedData();
        
        const [servicesRes, galleryRes, settingsRes, recentWorksRes] = await Promise.all([
          getServices(),
          getGallery(),
          getSettings(),
          getRecentWorks(),
        ]);
        setServices(servicesRes.data);
        setGallery(galleryRes.data);
        setSettings(settingsRes.data);
        setRecentWorks(recentWorksRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Initialize scroll reveal animations after data is loaded
  useScrollReveal([services, gallery, recentWorks]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1C]" data-testid="loading-spinner">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1C]" data-testid="home-page">
      <Navbar settings={settings} />
      <HeroSection settings={settings} />
      <ServicesSection services={services} />
      <GallerySection gallery={gallery} />
      <RecentWorksSection recentWorks={recentWorks} />
      <AboutSection settings={settings} />
      <ContactSection settings={settings} services={services} />
      <Footer settings={settings} />
    </div>
  );
}
